// src/store/slices/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";
import { setTenant, clearTenant } from "./tenantSlice";
import { clearPermissions, fetchMyPermissionsThunk } from "./permissionSlice";
import { fetchTenantTreeThunk } from "./userPanchayathAssignSlice";

const LS_DIST = "tenant_dist_id";
const LS_TALUK = "tenant_taluk_id";

export const loginThunk = createAsyncThunk(
  "auth/login",
  async ({ username, password, navigate }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/api/login", { username, password });
      const { success, token, user, message } = res.data || {};

      if (!success || !token || !user) {
        return rejectWithValue(message || "Invalid credentials");
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      const role = String(user.role || "").toLowerCase();
      localStorage.setItem("user_role", role);
      if (user?.uid != null) localStorage.setItem("user_id", String(user.uid));
      else if (user?.id != null) localStorage.setItem("user_id", String(user.id));

      dispatch(clearPermissions());

      // ✅ Normal user: tenant from token
      if (role !== "superadmin") {
        const pan_id = Number(user.pan_id || 0);
        if (!pan_id) throw new Error("pan_id missing");

        dispatch(
          setTenant({
            pan_id,
            pan_ids: [pan_id],
            pan_name: user.pan_name || "Panchayath",
          })
        );

        await dispatch(fetchMyPermissionsThunk()).unwrap();
        navigate("/dashboard", { replace: true });
        return { token, user };
      }

      // ✅ Superadmin: clear tenant; load tree only
      dispatch(clearTenant());
      localStorage.removeItem(LS_DIST);
      localStorage.removeItem(LS_TALUK);

      await dispatch(fetchTenantTreeThunk()).unwrap();

      // ❌ DO NOT fetch permissions here (wait tenant selection)
      navigate("/dashboard", { replace: true });
      return { token, user };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Login failed"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    token: localStorage.getItem("token"),
    user: (() => {
      try {
        return JSON.parse(localStorage.getItem("user"));
      } catch {
        return null;
      }
    })(),
    showLoading: false,
    errorMessage: "",
  },
  reducers: {
    logout(state) {
      state.token = null;
      state.user = null;

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("user_role");
      localStorage.removeItem("user_id");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.showLoading = true;
        state.errorMessage = "";
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.showLoading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.showLoading = false;
        state.errorMessage = action.payload;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
